/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import LangSwitch from "@/components/LangSwitch";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Check, X, Eye, EyeOff, Sprout, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FARM_OPTIONS = ["Bovins", "Ovins", "Caprins", "Lapins", "Autre"];

function rules(pw) {
  return { length: pw.length >= 10, upper: /[A-Z]/.test(pw), digit: /\d/.test(pw), special: /[^A-Za-z0-9]/.test(pw) };
}

export default function Signup() {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", farm_type: "Bovins", plan: "essentiel", billing_cycle: "monthly" });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);

  if (user) {
    return <Navigate to={user.role === "owner" ? "/dashboard/owner" : user.role === "trainer" ? "/dashboard/trainer" : "/dashboard/client"} replace />;
  }

  const r = rules(form.password);
  const allOk = r.length && r.upper && r.digit && r.special;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) {
      setError(t.app.signup.consent_must_accept);
      return;
    }
    setSubmitting(true);
    try {
      // Register: the backend sets httpOnly cookies for instant login + 1-month trial.
      await axios.post(
        `${API}/auth/register`,
        { ...form, email: form.email.trim().toLowerCase(), name: form.name.trim(), accept_terms: true },
        { withCredentials: true }
      );
      // Refresh AuthProvider so we know the user is logged in
      await refresh();
      setDone(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-6 py-10">
        <div data-testid="signup-success" className="max-w-lg w-full bg-white rounded-2xl border border-[#E2E4DF] p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto">
            <CheckCircle2 size={32} className="text-emerald-700" />
          </div>
          <h1 className="mt-5 font-display text-3xl font-black text-[#1C3A27]">{t.app.signup.success_title}</h1>
          <p className="mt-3 text-[#5C6B5C]">{t.app.signup.success_desc}</p>
          <div className="mt-7">
            <Button
              data-testid="signup-success-dashboard"
              onClick={() => navigate("/dashboard/client")}
              className="w-full h-12 rounded-xl bg-[#1C3A27] hover:bg-[#142A1C] text-white"
            >
              {t.app.signup.success_cta_dashboard}
            </Button>
          </div>
          <div className="mt-5 flex justify-center">
            <WhatsAppCTA
              data-testid="signup-success-whatsapp"
              variant="secondary"
              message={t.app.whatsapp.signup_msg
                .replace("{name}", form.name || "—")
                .replace("{farm}", t.contact.farm_options[form.farm_type] || form.farm_type)}
            />
          </div>
          <div className="mt-6">
            <Link to="/" className="text-sm font-semibold text-[#5C6B5C] hover:text-[#1C3A27]">{t.app.common.back_home}</Link>
          </div>
        </div>
      </div>
    );
  }

  const PLAN_OPTIONS = [
    { value: "essentiel", label: t.app.signup.plan_e },
    { value: "pro", label: t.app.signup.plan_p },
    { value: "domaine", label: t.app.signup.plan_d },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex items-stretch">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-[#1C3A27] text-white p-12 relative overflow-hidden">
        <div aria-hidden className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full opacity-[0.08] bg-[radial-gradient(#FAFAF7_1px,transparent_1px)] [background-size:14px_14px]" />
        <div className="flex items-center justify-between relative">
          <Link to="/" className="flex items-center gap-2 w-fit" data-testid="signup-back-home">
            <span className="w-8 h-8 rounded-full bg-white grid place-items-center"><span className="w-3 h-3 rounded-full bg-[#E05A3D]" /></span>
            <span className="font-display text-xl font-bold tracking-tight">Mazra3ti</span>
          </Link>
          <LangSwitch />
        </div>
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]"><Sprout size={14} /> {t.app.signup.side_overline}</div>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-black leading-tight">{t.app.signup.side_title_a}<br /> {t.app.signup.side_title_b}</h1>
          <p className="mt-5 text-white/70 text-lg max-w-md">{t.app.signup.side_desc}</p>
          <ul className="mt-8 space-y-3 text-white/90 text-sm">
            {t.app.signup.perks.map((p) => (
              <li key={p} className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-[#E05A3D] grid place-items-center"><Check size={12} strokeWidth={3} className="text-white" /></span>
                {p}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative text-sm text-white/60">© {new Date().getFullYear()} Mazra3ti</div>
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 md:px-16 py-12 relative">
        <div className="lg:hidden flex items-center justify-between mb-8">
          <Link to="/" className="flex items-center gap-2 text-sm text-[#5C6B5C]"><ArrowLeft size={16} /> {t.app.common.back_home}</Link>
          <LangSwitch variant="light" />
        </div>
        <div className="max-w-md w-full mx-auto">
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#1C3A27]">{t.app.signup.title}</h2>
          <p className="mt-2 text-[#5C6B5C]">{t.app.signup.subtitle}</p>

          <form onSubmit={submit} data-testid="signup-form" className="mt-8 space-y-5">
            <div>
              <Label htmlFor="su-name">{t.app.signup.full_name}</Label>
              <Input id="su-name" data-testid="signup-name" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mohamed Ben Ali" className="mt-2 h-12 bg-white border-[#E2E4DF] rounded-xl focus-visible:ring-[#E05A3D]" />
            </div>
            <div>
              <Label htmlFor="su-email">{t.app.signup.email}</Label>
              <Input id="su-email" data-testid="signup-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="mohamed@ferme.tn" className="mt-2 h-12 bg-white border-[#E2E4DF] rounded-xl focus-visible:ring-[#E05A3D]" />
            </div>
            <div>
              <Label htmlFor="su-password">{t.app.signup.password}</Label>
              <div className="relative mt-2">
                <Input id="su-password" data-testid="signup-password" type={show ? "text" : "password"} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-12 bg-white border-[#E2E4DF] rounded-xl focus-visible:ring-[#E05A3D] pe-10" />
                <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6B5C] hover:text-[#1C3A27]">
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <ul className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
                <Rule ok={r.length} text={t.app.signup.rule_length} />
                <Rule ok={r.upper} text={t.app.signup.rule_upper} />
                <Rule ok={r.digit} text={t.app.signup.rule_digit} />
                <Rule ok={r.special} text={t.app.signup.rule_special} />
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t.app.signup.farm}</Label>
                <Select value={form.farm_type} onValueChange={(v) => setForm({ ...form, farm_type: v })}>
                  <SelectTrigger data-testid="signup-farm-type" className="mt-2 h-12 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FARM_OPTIONS.map((f) => <SelectItem key={f} value={f}>{t.contact.farm_options[f]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t.app.signup.plan}</Label>
                <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v })}>
                  <SelectTrigger data-testid="signup-plan" className="mt-2 h-12 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLAN_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t.app.signup.billing}</Label>
              <Select value={form.billing_cycle} onValueChange={(v) => setForm({ ...form, billing_cycle: v })}>
                <SelectTrigger data-testid="signup-billing" className="mt-2 h-12 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">{t.app.clients_list.billing_monthly}</SelectItem>
                  <SelectItem value="annual">{t.app.clients_list.annual_minus_20}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && <div data-testid="signup-error" role="alert" className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">{error}</div>}

            <label className="flex items-start gap-3 text-sm text-[#1C3A27] cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="signup-consent-checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-[#1C3A27]/30 text-[#E05A3D] focus:ring-[#E05A3D] shrink-0"
              />
              <span className="leading-snug">
                {t.app.signup.consent_label_fr}{" "}
                <Link to="/legal/cgu" target="_blank" className="font-semibold underline decoration-[#E05A3D] hover:text-[#E05A3D]">
                  {t.app.signup.consent_cgu}
                </Link>{" "}
                {t.app.signup.consent_and}{" "}
                <Link to="/legal/privacy" target="_blank" className="font-semibold underline decoration-[#E05A3D] hover:text-[#E05A3D]">
                  {t.app.signup.consent_privacy}
                </Link>
                .
              </span>
            </label>

            <Button type="submit" data-testid="signup-submit" disabled={!allOk || !consent || submitting} className="w-full h-12 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold text-base group disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? t.app.signup.submitting : t.app.signup.submit}
              <ArrowRight size={18} className="ms-2 group-hover:translate-x-1 transition" />
            </Button>
            <p className="text-sm text-[#5C6B5C] text-center">
              {t.app.signup.have_account}{" "}
              <Link to="/login" className="font-semibold text-[#1C3A27] hover:text-[#E05A3D]">{t.app.signup.login_link}</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function Rule({ ok, text }) {
  return (
    <li className={`flex items-center gap-1.5 ${ok ? "text-emerald-700" : "text-[#5C6B5C]"}`}>
      {ok ? <Check size={13} /> : <X size={13} />}
      {text}
    </li>
  );
}
